import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  FormGroup,
  Checkbox,
} from "@mui/material";
import parse from "html-react-parser";

export interface FormStateConfig {
  formEmail: boolean;
  formPhone: boolean;
  formCompany: boolean;
  formGender: boolean;
  formContract?: string;
  formAddPermission?: string;
  formAddPermission2?: string;
  bonusField?: string;
}

export interface UserDataValues {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  company?: string;
  gender?: 1 | 2;
  formAddPermissionChecked: boolean;
  formAddPermission2Checked: boolean;
  [key: string]: any;
}

interface UserDataModalProps {
  open: boolean;
  onClose: () => void;
  formState?: FormStateConfig | null;

  /** Optional seed values (e.g., when editing or continuing) */
  initialValues?: Partial<UserDataValues>;

  /**
   * Called with normalized values when user submits.
   * Parent can create/patch Player and/or Game entities.
   */
  onSubmit: (values: UserDataValues) => Promise<boolean> | boolean;

  submitLabel?: string;

  /** Optional: set `required` on visible fields (defaults: true for name/lastname, phone/company if shown) */
  requireVisibleFields?: boolean;
}

const defaultFormState: FormStateConfig = {
  formEmail: false,
  formPhone: false,
  formCompany: false,
  formGender: false,
  bonusField: undefined,
};

const defaultBlank = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  company: "",
  gender: 1 as 1 | 2,
  formAddPermissionChecked: false,
  formAddPermission2Checked: false
};

export default function UserDataModal({
  open,
  onClose,
  formState,
  initialValues,
  onSubmit,
  submitLabel = "Gönder",
  requireVisibleFields = true,
}: UserDataModalProps) {
  const [values, setValues] = useState<UserDataValues>({ ...defaultBlank });
  const fs = formState ?? defaultFormState;
  const bonusKey = fs.bonusField as string | undefined;

  const [contractChecked, setContractChecked] = useState(false);
  const [contractVisited, setContractVisited] = useState(false);

  // Build initial with respect to dynamic bonus field key
  const initialWithBonus = useMemo(() => {
    const base: UserDataValues = {
      ...defaultBlank,
      ...(initialValues || {}),
    };
    if (bonusKey && (base as any)[bonusKey] === undefined) {
      (base as any)[bonusKey] = "";
    }
    return base;
  }, [initialValues, bonusKey]);

  useEffect(() => {
    setValues(initialWithBonus);

    setContractChecked(false);
    setContractVisited(false);
  }, [initialWithBonus, open]);

  const contractHref = useMemo(() => {
    if (!fs.formContract) return null;
    const div = document.createElement("div");
    div.innerHTML = fs.formContract;
    const a = div.querySelector("a[href]") as HTMLAnchorElement | null;
    return a?.href ?? null;
  }, [fs.formContract]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: name === "gender" ? (parseInt(value, 10) as 1 | 2) : value,
    }));
  };

  const handleCheckbox =
  (name: keyof UserDataValues) =>
  (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((prev) => ({
      ...prev,
      [name]: e.target.checked, // ✅ boolean, not string
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log(values);
    const ok = await onSubmit(values);
    if (ok) onClose();
  };

  const req = (shown: boolean) => (requireVisibleFields && shown ? true : false);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      className="game-modal modal"
    >
      <Box
        sx={{
          width: 400,
          bgcolor: "background.paper",
          maxHeight: "90vh",
          boxShadow: 24,
          p: 4,
          outline: "none",
          overflowY: "auto",
        }}
      >
        <p>Lütfen bilgilerinizi giriniz.</p>
        <form onSubmit={handleSubmit} className="login-form">
          <Grid className="form-namebox" container sx={{ justifyContent: "space-between" }}>
            <Grid item xs={5}>
              <TextField
                label="Adınız"
                name="firstName"
                type="text"
                fullWidth
                variant="outlined"
                value={values.firstName}
                onChange={handleChange}
                required={req(true)}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="Soyadınız"
                name="lastName"
                type="text"
                fullWidth
                variant="outlined"
                value={values.lastName}
                onChange={handleChange}
                required={req(true)}
              />
            </Grid>
          </Grid>

          {fs.formEmail && (
            <TextField
              label="E-Posta"
              name="email"
              type="email"
              fullWidth
              variant="outlined"
              value={values.email || ""}
              onChange={handleChange}
              required={req(fs.formEmail)}
              sx={{ mt: 2 }}
            />
          )}

          {fs.formPhone && (
            <TextField
              label="Telefon"
              name="phone"
              type="text"
              fullWidth
              variant="outlined"
              value={values.phone || ""}
              onChange={(e) => {
                let input = e.target.value.replace(/\D/g, "");

                if (!input.startsWith("0")) {
                  input = "0" + input;
                }

                if (input.length > 11) {
                  input = input.slice(0, 11);
                }

                setValues((prev) => ({
                  ...prev,
                  phone: input,
                }));
              }}
              required={req(fs.formPhone)}
              inputProps={{
                pattern: "^0\\d{10}$",
                title: "Telefon numarası 0 ile başlamalı ve toplam 11 haneli olmalıdır",
              }}
              sx={{ mt: 2 }}
            />
          )}

          {fs.formCompany && (
            <TextField
              label="Firma Adı"
              name="company"
              type="text"
              fullWidth
              variant="outlined"
              value={values.company || ""}
              onChange={handleChange}
              required={req(fs.formCompany)}
              sx={{ mt: 2 }}
            />
          )}

          {fs.formGender && (
            <>
              <Typography variant="subtitle2" sx={{ textAlign: "left", mt: 2 }}>
                Cinsiyet
              </Typography>
              <RadioGroup
                row
                aria-labelledby="gender-group"
                name="gender"
                value={values.gender}
                onChange={handleChange}
              >
                <FormControlLabel value="1" control={<Radio />} label="Erkek" />
                <FormControlLabel value="2" control={<Radio />} label="Kadın" />
              </RadioGroup>
            </>
          )}

          {bonusKey && (
            <TextField
              label={bonusKey}
              name={bonusKey}
              type="text"
              fullWidth
              variant="outlined"
              value={(values as any)[bonusKey] || ""}
              onChange={handleChange}
              required={req(true)}
              sx={{ mt: 2 }}
            />
          )}

          <FormGroup className="form-control-contracts" sx={{ mt: 2 }}>
            {fs.formContract && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contractChecked}
                    onChange={(e) => {
                      // trying to check?
                      if (e.target.checked && !contractVisited && contractHref) {
                        window.open(contractHref, "_blank", "noopener");
                        setContractVisited(true);
                        // keep it UNchecked on this first click
                        return;
                      }
                      setContractChecked(e.target.checked);
                    }}
                    inputProps={{
                      required: true,
                      name: "contractConsent",
                    }}
                  />
                }
                label={<small>{parse(fs.formContract)}</small>}
              />
            )}
            {fs.formAddPermission && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!values.formAddPermissionChecked}
                    onChange={handleCheckbox("formAddPermissionChecked")}
                  />
                }
                label={<small>{fs.formAddPermission}</small>}
              />
            )}
            {fs.formAddPermission2 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!values.formAddPermission2Checked}
                    onChange={handleCheckbox("formAddPermission2Checked")}
                  />
                }
                label={<small>{fs.formAddPermission2}</small>}
              />
            )}
          </FormGroup>

          <Button variant="contained" type="submit" sx={{ my: 3, backgroundColor: "#FF6100" }}>
            {submitLabel}
          </Button>
        </form>
      </Box>
    </Modal>
  );
}
